// ═══════════════════════════════════════════════════════════════════════════════
// Previous-pairing import — shared selection UI (§0.50)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  TASK_GROUP_CONFIG_TYPE,
  TASK_KIND,
  TASK_STATUS,
  type PairingImportPreview,
} from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../ui/ToastProvider';
import { AppButton } from '../ui/AppButton';
import { pairingImportApi } from '../../lib/api';
import {
  canSelectImportPin,
  canSelectImportTask,
  canSelectImportTaskGroup,
  computeSelectedTaskSlotCost,
} from '../../lib/pairingImportQuota';

type ImportPreviousPairingContentProps = {
  showLaterButton?: boolean;
  onLater?: () => void;
  onImportSuccess?: () => void;
  /** When true, render without outer card padding (modal supplies its own). */
  embedded?: boolean;
  /** When true, omit in-content title (page header already shows it). */
  hideTitle?: boolean;
};

const resolveTaskKindLabel = (
  taskKind: string,
  t: (key: string) => string,
): string => {
  if (taskKind === TASK_KIND.INVENTORY) {
    return t('task.list.kindStockTake');
  }
  if (taskKind === TASK_KIND.PICKUP_DROPOFF) {
    return t('task.list.kindPickupDropoff');
  }
  if (taskKind === TASK_KIND.MEDICATION_REMINDER) {
    return t('task.list.kindMedication');
  }
  return t('pairing.import.taskKindStandard');
};

const resolveGroupKindLabel = (
  configType: string,
  t: (key: string) => string,
): string => {
  if (configType === TASK_GROUP_CONFIG_TYPE.PICKUP_DROPOFF) {
    return t('task.list.kindPickupDropoff');
  }
  return t('task.list.kindMedication');
};

export const ImportPreviousPairingContent = ({
  showLaterButton = false,
  onLater,
  onImportSuccess,
  embedded = false,
  hideTitle = false,
}: ImportPreviousPairingContentProps) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { showSuccessToast, showErrorToast } = useToast();

  const [preview, setPreview] = useState<PairingImportPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [selectedTaskGroupIds, setSelectedTaskGroupIds] = useState<Set<string>>(new Set());
  const [selectedFamilyMemberIds, setSelectedFamilyMemberIds] = useState<Set<string>>(new Set());
  const [selectedPinMessageIds, setSelectedPinMessageIds] = useState<Set<string>>(new Set());
  const [familyConsentConfirmed, setFamilyConsentConfirmed] = useState(false);

  const loadPreview = useCallback(async (options?: { quiet?: boolean }): Promise<void> => {
    const quiet = options?.quiet === true;
    if (!quiet) {
      setLoading(true);
    }
    setLoadError(null);
    try {
      const res = await pairingImportApi.getPreview();
      if (res.success && res.data) {
        setPreview(res.data);
        setSelectedTaskIds(new Set());
        setSelectedTaskGroupIds(new Set());
        setSelectedFamilyMemberIds(new Set());
        setSelectedPinMessageIds(new Set());
        setFamilyConsentConfirmed(false);
      } else {
        setLoadError(res.error ?? t('pairing.import.loadError'));
      }
    } catch {
      setLoadError(t('pairing.import.loadError'));
    } finally {
      if (!quiet) {
        setLoading(false);
      }
    }
  }, [t]);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  const hasAnyItems = useMemo(() => {
    if (!preview) {
      return false;
    }
    return (
      preview.tasks.length > 0 ||
      preview.taskGroups.length > 0 ||
      preview.familyMembers.length > 0 ||
      preview.pinMessages.length > 0
    );
  }, [preview]);

  const hasSelectableItems = useMemo(() => {
    if (!preview) {
      return false;
    }
    const anyTasks = preview.tasks.some((item) => !item.alreadyImported);
    const anyGroups = preview.taskGroups.some((item) => !item.alreadyImported);
    const anyFamily = preview.familyMembers.some((item) => !item.alreadyImported);
    const anyPins = preview.pinMessages.some((item) => !item.alreadyImported);
    return anyTasks || anyGroups || anyFamily || anyPins;
  }, [preview]);

  const selectedCount =
    selectedTaskIds.size +
    selectedTaskGroupIds.size +
    selectedFamilyMemberIds.size +
    selectedPinMessageIds.size;

  const needsFamilyConsent = selectedFamilyMemberIds.size > 0;

  const canSubmitImport =
    hasSelectableItems &&
    selectedCount > 0 &&
    (!needsFamilyConsent || familyConsentConfirmed) &&
    !busy &&
    !loading;

  const taskQuotaHint = useMemo(() => {
    if (!preview || preview.taskSlotsMax === null) {
      return null;
    }
    const selectedCost = computeSelectedTaskSlotCost(preview, selectedTaskIds, selectedTaskGroupIds);
    return t('pairing.import.taskQuotaHint', {
      used: preview.taskSlotsUsed + selectedCost,
      max: preview.taskSlotsMax,
    });
  }, [preview, selectedTaskGroupIds, selectedTaskIds, t]);

  const pinQuotaHint = useMemo(() => {
    if (!preview) {
      return null;
    }
    const selectedPinCount = selectedPinMessageIds.size;
    return t('pairing.import.pinQuotaHint', {
      used: preview.pinTotalCount + selectedPinCount,
      max: preview.pinMaxItems,
    });
  }, [preview, selectedPinMessageIds, t]);

  const handleToggleTask = (taskId: string): void => {
    if (!preview || busy) {
      return;
    }
    const task = preview.tasks.find((item) => item.id === taskId);
    if (!task || task.alreadyImported) {
      return;
    }
    setSelectedTaskIds((prev) => {
      if (prev.has(taskId)) {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      }
      if (!canSelectImportTask(preview, taskId, prev, selectedTaskGroupIds)) {
        return prev;
      }
      return new Set([...prev, taskId]);
    });
  };

  const handleToggleTaskGroup = (groupId: string): void => {
    if (!preview || busy) {
      return;
    }
    const group = preview.taskGroups.find((item) => item.id === groupId);
    if (!group || group.alreadyImported) {
      return;
    }
    setSelectedTaskGroupIds((prev) => {
      if (prev.has(groupId)) {
        const next = new Set(prev);
        next.delete(groupId);
        return next;
      }
      if (!canSelectImportTaskGroup(preview, groupId, selectedTaskIds, prev)) {
        return prev;
      }
      return new Set([...prev, groupId]);
    });
  };

  const handleToggleFamilyMember = (memberId: string): void => {
    if (busy) {
      return;
    }
    const member = preview?.familyMembers.find((item) => item.id === memberId);
    if (!member || member.alreadyImported) {
      return;
    }
    setSelectedFamilyMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  };

  const handleTogglePin = (pinId: string): void => {
    if (!preview || busy) {
      return;
    }
    const pin = preview.pinMessages.find((item) => item.id === pinId);
    if (!pin || pin.alreadyImported) {
      return;
    }
    setSelectedPinMessageIds((prev) => {
      if (prev.has(pinId)) {
        const next = new Set(prev);
        next.delete(pinId);
        return next;
      }
      if (!canSelectImportPin(preview, pinId, prev, user?.level)) {
        return prev;
      }
      return new Set([...prev, pinId]);
    });
  };

  const handleFamilyConsentChange = (): void => {
    setFamilyConsentConfirmed((prev) => !prev);
  };

  const handleImport = async (): Promise<void> => {
    if (!canSubmitImport) {
      return;
    }
    setBusy(true);
    try {
      const res = await pairingImportApi.import({
        taskIds: [...selectedTaskIds],
        taskGroupIds: [...selectedTaskGroupIds],
        familyMemberIds: [...selectedFamilyMemberIds],
        pinMessageIds: [...selectedPinMessageIds],
        familyConsentConfirmed: needsFamilyConsent ? true : undefined,
      });
      if (!res.success) {
        showErrorToast(res.error ?? t('pairing.import.error'));
        return;
      }
      showSuccessToast(t('pairing.import.success'));
      // Refresh so imported rows become gray / untickable on this page.
      await loadPreview({ quiet: true });
      onImportSuccess?.();
    } catch {
      showErrorToast(t('pairing.import.error'));
    } finally {
      setBusy(false);
    }
  };

  const handleLater = (): void => {
    if (busy) {
      return;
    }
    onLater?.();
  };

  useEffect(() => {
    if (selectedFamilyMemberIds.size === 0) {
      setFamilyConsentConfirmed(false);
    }
  }, [selectedFamilyMemberIds.size]);

  const renderCheckboxRow = (
    inputId: string,
    checked: boolean,
    disabled: boolean,
    onToggle: () => void,
    title: string,
    badges: ReactNode,
    subtitle?: string,
  ): ReactNode => (
    <li key={inputId}>
      <label
        htmlFor={inputId}
        className={[
          'flex items-start gap-3 rounded-xl border p-3',
          disabled
            ? 'cursor-not-allowed border-gray-200 bg-gray-100/80 opacity-70 dark:border-gray-700 dark:bg-gray-800/40'
            : 'cursor-pointer border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60',
        ].join(' ')}
      >
        <input
          id={inputId}
          type="checkbox"
          checked={checked}
          disabled={disabled || busy}
          onChange={onToggle}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-900 dark:text-gray-100">{title}</span>
            {badges}
          </span>
          {subtitle ? (
            <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">{subtitle}</span>
          ) : null}
        </span>
      </label>
    </li>
  );

  const content = (
    <>
      {hideTitle ? null : (
        <h2
          id={embedded ? 'import-previous-pairing-title' : undefined}
          className="text-lg font-semibold text-gray-900 dark:text-gray-100"
        >
          {t('pairing.import.title')}
        </h2>
      )}
      <p
        className={[
          'text-sm text-gray-600 dark:text-gray-300',
          hideTitle ? '' : 'mt-2',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {t('pairing.import.description')}
      </p>

      {loading ? (
        <p className="mt-6 text-sm text-gray-500 dark:text-gray-400" role="status">
          {t('common.loading')}
        </p>
      ) : null}

      {loadError ? (
        <div className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300" role="alert">
          {loadError}
        </div>
      ) : null}

      {!loading && !loadError && preview && !hasAnyItems ? (
        <p className="mt-4 text-sm text-gray-600 dark:text-gray-300" role="status">
          {t('pairing.import.empty')}
        </p>
      ) : null}

      {!loading && !loadError && preview && hasAnyItems && !hasSelectableItems ? (
        <p className="mt-4 text-sm text-gray-600 dark:text-gray-300" role="status">
          {t('pairing.import.empty')}
        </p>
      ) : null}

      {!loading && !loadError && preview && hasAnyItems ? (
        <div className="mt-4 space-y-5">
          {taskQuotaHint && hasSelectableItems ? (
            <p className="text-xs text-gray-500 dark:text-gray-400" role="status">
              {taskQuotaHint}
            </p>
          ) : null}

          {preview.taskGroups.length > 0 || preview.tasks.length > 0 ? (
            <section aria-labelledby="import-section-tasks">
              <h3 id="import-section-tasks" className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                {t('pairing.import.sectionTasks')}
              </h3>
              <ul className="mt-2 max-h-[40vh] space-y-2 overflow-y-auto" role="list">
                {preview.taskGroups.map((group) => {
                  const inputId = `import-group-${group.id}`;
                  const checked = selectedTaskGroupIds.has(group.id);
                  const quotaBlocked =
                    !group.alreadyImported &&
                    !checked &&
                    !canSelectImportTaskGroup(preview, group.id, selectedTaskIds, selectedTaskGroupIds);
                  const disabled = group.alreadyImported || quotaBlocked;
                  return renderCheckboxRow(
                    inputId,
                    checked,
                    disabled,
                    () => handleToggleTaskGroup(group.id),
                    group.title,
                    <>
                      <span className="inline-flex rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-800 dark:bg-sky-950/50 dark:text-sky-200">
                        {resolveGroupKindLabel(group.configType, t)}
                      </span>
                      {group.alreadyImported ? (
                        <span className="inline-flex rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                          {t('pairing.import.alreadyImported')}
                        </span>
                      ) : null}
                    </>,
                  );
                })}
                {preview.tasks.map((task) => {
                  const inputId = `import-task-${task.id}`;
                  const checked = selectedTaskIds.has(task.id);
                  const quotaBlocked =
                    !task.alreadyImported &&
                    !checked &&
                    !canSelectImportTask(preview, task.id, selectedTaskIds, selectedTaskGroupIds);
                  const disabled = task.alreadyImported || quotaBlocked;
                  return renderCheckboxRow(
                    inputId,
                    checked,
                    disabled,
                    () => handleToggleTask(task.id),
                    task.title,
                    <>
                      <span className="inline-flex rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-800 dark:bg-sky-950/50 dark:text-sky-200">
                        {resolveTaskKindLabel(task.taskKind, t)}
                      </span>
                      {task.status === TASK_STATUS.ARCHIVED ? (
                        <span className="inline-flex rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                          {t('task.action.archive')}
                        </span>
                      ) : null}
                      {task.alreadyImported ? (
                        <span className="inline-flex rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                          {t('pairing.import.alreadyImported')}
                        </span>
                      ) : null}
                    </>,
                  );
                })}
              </ul>
            </section>
          ) : null}

          {preview.familyMembers.length > 0 ? (
            <section aria-labelledby="import-section-family">
              <h3 id="import-section-family" className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                {t('pairing.import.sectionFamily')}
              </h3>
              <ul className="mt-2 space-y-2" role="list">
                {preview.familyMembers.map((member) => {
                  const inputId = `import-family-${member.id}`;
                  const checked = selectedFamilyMemberIds.has(member.id);
                  const disabled = member.alreadyImported;
                  const subtitle =
                    member.nickname.trim() !== '' && member.nickname.trim() !== member.name.trim()
                      ? member.nickname
                      : undefined;
                  return renderCheckboxRow(
                    inputId,
                    checked,
                    disabled,
                    () => handleToggleFamilyMember(member.id),
                    member.name,
                    member.alreadyImported ? (
                      <span className="inline-flex rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                        {t('pairing.import.alreadyImported')}
                      </span>
                    ) : null,
                    subtitle,
                  );
                })}
              </ul>
            </section>
          ) : null}

          {preview.pinMessages.length > 0 ? (
            <section aria-labelledby="import-section-pins">
              <h3 id="import-section-pins" className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                {t('pairing.import.sectionPins')}
              </h3>
                  {pinQuotaHint && hasSelectableItems ? (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" role="status">
                  {pinQuotaHint}
                </p>
              ) : null}
              <ul className="mt-2 max-h-[40vh] space-y-2 overflow-y-auto" role="list">
                {preview.pinMessages.map((pin) => {
                  const inputId = `import-pin-${pin.id}`;
                  const checked = selectedPinMessageIds.has(pin.id);
                  const quotaBlocked =
                    !pin.alreadyImported &&
                    !checked &&
                    !canSelectImportPin(preview, pin.id, selectedPinMessageIds, user?.level);
                  const disabled = pin.alreadyImported || quotaBlocked;
                  return renderCheckboxRow(
                    inputId,
                    checked,
                    disabled,
                    () => handleTogglePin(pin.id),
                    pin.title,
                    <>
                      <span
                        className={[
                          'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                          pin.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200'
                            : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
                        ].join(' ')}
                      >
                        {pin.isActive ? t('pinMessage.status.active') : t('pinMessage.status.inactive')}
                      </span>
                      {pin.alreadyImported ? (
                        <span className="inline-flex rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                          {t('pairing.import.alreadyImported')}
                        </span>
                      ) : null}
                    </>,
                  );
                })}
              </ul>
            </section>
          ) : null}

          {needsFamilyConsent ? (
            <label
              htmlFor="import-family-consent"
              className="flex cursor-pointer items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/80 p-3 dark:border-amber-900/50 dark:bg-amber-950/30"
            >
              <input
                id="import-family-consent"
                type="checkbox"
                checked={familyConsentConfirmed}
                disabled={busy}
                onChange={handleFamilyConsentChange}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-amber-950 dark:text-amber-100/90">
                {t('pairing.import.consentLabel')}
              </span>
            </label>
          ) : null}
        </div>
      ) : null}

      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {showLaterButton ? (
          <AppButton type="button" variant="secondary" onClick={handleLater} disabled={busy}>
            {t('pairing.import.later')}
          </AppButton>
        ) : null}
        <AppButton
          type="button"
          variant="primary"
          onClick={() => {
            void handleImport();
          }}
          disabled={!canSubmitImport}
        >
          {busy ? t('common.loading') : t('pairing.import.import')}
        </AppButton>
      </div>
    </>
  );

  if (embedded) {
    return content;
  }

  // Full page: parent supplies outer padding; only the content card remains.
  if (hideTitle) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        {content}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-6">
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        {content}
      </div>
    </div>
  );
};
